// src/data/worksheets/anxiety.js
//
// Interactive versions of the EAS "Anxiety Management" checklist + worksheet
// (converted from the PDF pair). Schema: see ./index.js.

export const ANXIETY_CHECKLIST = {
    id: 'anxiety_checklist',
    topicId: 'anxiety',
    kind: 'checklist',
    title: 'Anxiety Management Checklist',
    intro: 'Tick things off as you do them. Come back and review it regularly — it saves as you go.',
    sections: [
        {
            id: 'immediate', title: 'Immediate Steps',
            prompts: [
                { id: 'acknowledge', type: 'checklist', label: 'Acknowledge and Accept', items: [
                    { id: 'a1', label: 'Recognize the signs of anxiety.' },
                    { id: 'a2', label: 'Accept that anxiety is a common and treatable condition.' }
                ] },
                { id: 'help', type: 'checklist', label: 'Seek Professional Help', items: [
                    { id: 'h1', label: 'Consult with a mental health professional (therapist, counselor, psychologist).' },
                    { id: 'h2', label: 'If available, speak with a {sports psychologist|workplace counsellor or psychologist}.' }
                ] }
            ]
        },
        {
            id: 'routine', title: 'Daily Routine Adjustments',
            prompts: [
                { id: 'sleep', type: 'checklist', label: 'Establish a Regular Sleep Schedule', items: [
                    { id: 's1', label: 'Aim for 7–9 hours of sleep each night.' },
                    { id: 's2', label: 'Create a bedtime routine to wind down.' }
                ] },
                { id: 'nutrition', type: 'checklist', label: 'Nutrition and Hydration', items: [
                    { id: 'n1', label: 'Maintain a balanced diet rich in vitamins and minerals.' },
                    { id: 'n2', label: 'Stay hydrated throughout the day.' }
                ] },
                { id: 'activity', type: 'checklist', label: 'Physical Activity', items: [
                    { id: 'p1', label: '{Continue regular training but be mindful of not over-training.|Keep moving regularly, but be mindful of not overdoing it.}' },
                    { id: 'p2', label: 'Incorporate relaxing physical activities like yoga or stretching.' }
                ] },
                { id: 'mindfulness', type: 'checklist', label: 'Mindfulness and Relaxation Techniques', items: [
                    { id: 'm1', label: 'Practice mindfulness or meditation daily.' },
                    { id: 'm2', label: 'Engage in deep breathing exercises.' },
                    { id: 'm3', label: 'Consider progressive muscle relaxation.' }
                ] },
                { id: 'time', type: 'checklist', label: 'Time Management and Organization', items: [
                    { id: 't1', label: 'Create a daily schedule to reduce stress from time pressures.' },
                    { id: 't2', label: 'Set realistic goals and prioritize tasks.' }
                ] }
            ]
        },
        {
            id: 'practices', title: 'Mental Health Practices',
            prompts: [
                { id: 'cbt', type: 'checklist', label: 'Cognitive Behavioral Techniques', items: [
                    { id: 'c1', label: 'Work on identifying and challenging negative thoughts.' },
                    { id: 'c2', label: 'Practice positive self-talk and affirmations.' }
                ] },
                { id: 'journaling', type: 'checklist', label: 'Journaling', items: [
                    { id: 'j1', label: 'Keep a journal to express thoughts and feelings.' },
                    { id: 'j2', label: 'Track anxiety triggers and patterns.' }
                ] },
                { id: 'support', type: 'checklist', label: 'Support System', items: [
                    { id: 'u1', label: 'Reach out to friends, family, or {teammates|colleagues} for support.' },
                    { id: 'u2', label: 'Join a support group if available.' }
                ] }
            ]
        },
        {
            id: 'professional', title: 'Professional Interventions',
            prompts: [
                { id: 'therapy', type: 'checklist', label: 'Therapy Sessions', items: [
                    { id: 'th1', label: 'Attend regular therapy sessions as recommended.' },
                    { id: 'th2', label: 'Engage fully in therapeutic exercises and homework.' }
                ] },
                { id: 'medication', type: 'checklist', label: 'Medication (if prescribed)', items: [
                    { id: 'md1', label: 'Take prescribed medication consistently.' },
                    { id: 'md2', label: 'Communicate with your doctor about any side effects.' }
                ] }
            ]
        },
        {
            id: 'performance', title: 'Performance-Specific Strategies',
            prompts: [
                { id: 'precomp', type: 'checklist', label: '{Pre-Competition Routine|Pre-Meeting Routine}', items: [
                    { id: 'pc1', label: 'Develop a calming {pre-competition routine|pre-meeting routine}.' },
                    { id: 'pc2', label: 'Use visualization techniques to imagine successful outcomes.' }
                ] },
                { id: 'focus', type: 'checklist', label: 'Focus Techniques', items: [
                    { id: 'f1', label: 'Practice staying in the moment {during training and competition|during focused work and high-stakes moments}.' },
                    { id: 'f2', label: 'Use focus cues or keywords to maintain concentration.' }
                ] }
            ]
        },
        {
            id: 'longterm', title: 'Long-Term Strategies',
            prompts: [
                { id: 'checkins', type: 'checklist', label: 'Regular Check-ins with a Mental Health Professional', items: [
                    { id: 'ci1', label: 'Schedule periodic check-ins to monitor progress.' },
                    { id: 'ci2', label: 'Adjust treatment plans as necessary.' }
                ] },
                { id: 'learning', type: 'checklist', label: 'Continual Learning', items: [
                    { id: 'l1', label: 'Stay informed about anxiety and mental health.' },
                    { id: 'l2', label: 'Read books, attend workshops, or listen to podcasts on mental well-being.' }
                ] }
            ]
        },
        {
            id: 'review', title: 'Review & Reflect',
            prompts: [
                { id: 'selfassess', type: 'checklist', label: 'Regular Self-Assessment', items: [
                    { id: 'r1', label: 'Periodically review progress and adjust strategies as needed.' },
                    { id: 'r2', label: 'Celebrate small victories and progress made.' }
                ] }
            ]
        }
    ],
    outro: 'This covers immediate actions, daily routines, mental health practices and long-term strategies. Reviewing and adapting it regularly is what makes it work.'
};

export const ANXIETY_WORKSHEET = {
    id: 'anxiety_worksheet',
    topicId: 'anxiety',
    kind: 'worksheet',
    title: 'Anxiety Management Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        {
            id: 'self', title: 'Self Assessment',
            prompts: [
                { id: 'level', type: 'scale', label: 'Evaluate your anxiety levels', help: 'On a scale of 1 to 10, how would you rate your overall anxiety levels in the past week?', min: 1, max: 10, minLabel: 'No anxiety', maxLabel: 'Severe anxiety' },
                { id: 'triggers', type: 'list', label: 'Identify anxiety triggers', help: 'What situations or events tend to trigger your anxiety? (e.g., {competitions, training sessions, personal issues|big meetings, deadlines, personal issues})', count: 3 },
                { id: 'physical', type: 'list', label: 'Physical symptoms of anxiety', help: 'List any physical symptoms you experience when you feel anxious (e.g., increased heart rate, sweating, muscle tension).', count: 3 },
                { id: 'behavioral', type: 'list', label: 'Behavioral responses to anxiety', help: 'How do you typically respond to anxiety? (e.g., avoidance, overtraining, irritability)', count: 3 }
            ]
        },
        {
            id: 'understanding', title: 'Understanding Anxiety',
            prompts: [
                { id: 'thoughts', type: 'list', label: 'Recognize negative thought patterns', help: 'Identify any negative thoughts or beliefs that contribute to your anxiety (e.g., "I must be perfect," "I can\'t handle failure").', count: 3 },
                { id: 'impact', type: 'text', label: 'Impact on performance', help: 'Describe how anxiety affects your performance {in your sport|at work}.', multiline: true }
            ]
        },
        {
            id: 'managing', title: 'Managing Anxiety',
            prompts: [
                { id: 'coping', type: 'list', label: 'Develop coping strategies', help: 'List three healthy coping strategies you can use to manage anxiety (e.g., deep breathing, visualization, positive self-talk).', count: 3 },
                { id: 'relaxation', type: 'list', label: 'Relaxation techniques', help: 'Identify two relaxation techniques you can incorporate into your daily routine (e.g., meditation, progressive muscle relaxation).', count: 2 },
                { id: 'mindfulness', type: 'text', label: 'Mindfulness practices', help: 'How can you incorporate mindfulness into {your training and daily life|your work and daily life}? (e.g., mindful breathing, body scan meditation)', multiline: true },
                { id: 'affirmations', type: 'list', label: 'Positive self-talk', help: 'Develop a list of positive affirmations to counter negative thoughts and boost your confidence.', count: 3 }
            ]
        },
        {
            id: 'plan', title: 'Action Plan',
            prompts: [
                { id: 'goal_short', type: 'text', label: 'Short-term goal', help: 'One goal to help manage your anxiety in the next couple of weeks.' },
                { id: 'goal_long', type: 'text', label: 'Long-term goal', help: 'One goal for {the season / the year|the quarter / the year}.' },
                { id: 'support', type: 'list', label: 'Seek support', help: 'Identify individuals who can provide support and encouragement (e.g., {coach|manager}, {teammate|colleague}, {sports psychologist|workplace counsellor or psychologist}).', count: 2 }
            ]
        },
        {
            id: 'monitor', title: 'Monitor Your Progress',
            description: 'Track your anxiety levels and how well your coping strategies work over the next two weeks.',
            prompts: [
                {
                    id: 'tracker', type: 'table', label: 'Two-week tracker',
                    columns: [
                        { id: 'day', label: 'Day', kind: 'date' },
                        { id: 'mood', label: 'Mood (1–10)', kind: 'number', min: 1, max: 10 },
                        { id: 'strategy', label: 'Coping strategy used', kind: 'text' },
                        { id: 'effectiveness', label: 'Effectiveness (1–10)', kind: 'number', min: 1, max: 10 },
                        { id: 'notes', label: 'Notes', kind: 'text' }
                    ],
                    maxRows: 31,
                    chart: { x: 'day', series: [{ id: 'mood', label: 'Mood' }, { id: 'effectiveness', label: 'Effectiveness' }] }
                }
            ]
        }
    ],
    outro: 'Managing anxiety is a continuous process that takes self-awareness, practice and persistence. Use this regularly to monitor your progress, refine your strategies and notice your wins.',
    supportNote: 'If anxiety persists or is getting in the way of daily life, talking to someone is a good next step.'
};

export const ANXIETY_WORKSHEETS = [ANXIETY_CHECKLIST, ANXIETY_WORKSHEET];
